/**
 * LotMgtFormDialog — bám Access spec `frmFactoryQryLotMgtAdd`
 * (`plan/access_all/form_frmFactoryQryLotMgtAdd.txt`). Structure mirror
 * `IssueFormDialog` (same New/Edit dialog pattern), fields theo Lot Mgt spec.
 *
 * Layout: giữ nguyên thứ tự field (index order) theo Access TabIndex, FE đổ vào
 * GRID 3-COL column-major (12 field/cột, MAIN_FIELDS render theo thứ tự → DOM
 * order = Tab order). Contract tách xuống HÀNG RIÊNG dưới cùng (4-col).
 *   Col 1 (R1-12): lot_id(*), lot_name, lot_area(*), no_of_bag, quantity_mt,
 *                  cuptest, mc_rate, blacks_rate┐, brokens_rate┘→black_and_broken_rate,
 *                  black_and_broken_rate(auto), fm_rate, mouldy_rate
 *   Col 2 (R1-12): brown_rate, partly_black_rate, immature_rate,
 *                  broken_over_1_per_2_rate, insect_beans_rate, triages_defects_rate,
 *                  fb_rate, overdry_rate, cherry_rate, yellowbeans_rate,
 *                  polish_degree_rate, s20_rate
 *   Col 3 (R1-8):  s19_rate, s18_rate, s16_rate, s15_rate, s14_rate, s13_rate,
 *                  s12_rate, under_s12_rate
 *   Bottom row:    contract_no (combo, load suggest qua API GET lot-mgt-contracts),
 *                  contract_quality / contract_client / contract_package (readonly
 *                  DLookUp public.mv_local_s — auto-fill khi chọn contract_no).
 *
 * Hidden (`Visible = NotDefault`): no_of_cont, shipped_cont, actual_shipped_mt,
 *   id, id2 → KHÔNG render (BE giữ giá trị cũ / default).
 * Audit footer (`Enabled = NotDefault`, Top=7680): created/updated_by/_date,
 *   is_status → BE tự fill.
 * Required (Form_Load): `strCheck = ",lot_id,lot_area,"`.
 * Cascade (blacks_rate / brokens_rate AfterUpdate):
 *   black_and_broken_rate = Nz(blacks_rate,0) + Nz(brokens_rate,0).
 * contract_quality / contract_client / contract_package: Access DLookUp display
 *   từ public_mv_local_s theo contract_no (KHÔNG nằm trong strU/strI → không
 *   lưu). FE render readonly, hydrate best-effort từ row khi Edit; không có
 *   lookup endpoint nên New mode để trống.
 */
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import {
  useGetLotMgtContractsQuery,
  type FactoryRowPayload,
  type FactoryCell,
} from '@/store/api/factoryApi'

type LotMgtRow = { id: string | number } & Record<string, FactoryCell>

/** Editable numeric fields (Access Format Standard `#,##0.00`). */
const NUMERIC_KEYS = [
  'no_of_bag',
  'quantity_mt',
  'mc_rate',
  'blacks_rate',
  'brokens_rate',
  'black_and_broken_rate',
  'fm_rate',
  'mouldy_rate',
  'brown_rate',
  'partly_black_rate',
  'immature_rate',
  'broken_over_1_per_2_rate',
  'insect_beans_rate',
  'triages_defects_rate',
  'fb_rate',
  'overdry_rate',
  'cherry_rate',
  'yellowbeans_rate',
  'polish_degree_rate',
  's20_rate',
  's19_rate',
  's18_rate',
  's16_rate',
  's15_rate',
  's14_rate',
  's13_rate',
  's12_rate',
  'under_s12_rate',
] as const

type NumericKey = (typeof NUMERIC_KEYS)[number]

interface DraftState {
  lot_id: string
  lot_name: string
  lot_area: string
  cuptest: string
  contract_no: string
  no_of_bag: string
  quantity_mt: string
  mc_rate: string
  blacks_rate: string
  brokens_rate: string
  black_and_broken_rate: string
  fm_rate: string
  mouldy_rate: string
  brown_rate: string
  partly_black_rate: string
  immature_rate: string
  broken_over_1_per_2_rate: string
  insect_beans_rate: string
  triages_defects_rate: string
  fb_rate: string
  overdry_rate: string
  cherry_rate: string
  yellowbeans_rate: string
  polish_degree_rate: string
  s20_rate: string
  s19_rate: string
  s18_rate: string
  s16_rate: string
  s15_rate: string
  s14_rate: string
  s13_rate: string
  s12_rate: string
  under_s12_rate: string
}

const EMPTY_DRAFT: DraftState = {
  lot_id: '',
  lot_name: '',
  lot_area: '',
  cuptest: '',
  contract_no: '',
  no_of_bag: '',
  quantity_mt: '',
  mc_rate: '',
  blacks_rate: '',
  brokens_rate: '',
  black_and_broken_rate: '',
  fm_rate: '',
  mouldy_rate: '',
  brown_rate: '',
  partly_black_rate: '',
  immature_rate: '',
  broken_over_1_per_2_rate: '',
  insect_beans_rate: '',
  triages_defects_rate: '',
  fb_rate: '',
  overdry_rate: '',
  cherry_rate: '',
  yellowbeans_rate: '',
  polish_degree_rate: '',
  s20_rate: '',
  s19_rate: '',
  s18_rate: '',
  s16_rate: '',
  s15_rate: '',
  s14_rate: '',
  s13_rate: '',
  s12_rate: '',
  under_s12_rate: '',
}

/** Main fields (trừ contract) theo ĐÚNG thứ tự index, column-major:
 *  col1 = field 1-12, col2 = 13-24, col3 = 25-32. Render theo thứ tự này để
 *  DOM order == thứ tự Tab → Tab nhảy field top→bottom đúng từng cột (fix bug
 *  "Lot Area nhảy xuống Cuptest"). Contract field tách xuống hàng riêng dưới cùng. */
const ROWS_PER_COL = 12

interface MainField {
  key: keyof DraftState
  label: string
  kind: 'text' | 'combo' | 'numeric'
  required?: boolean
  pk?: boolean // lot_id — PK, disabled khi edit
  cascade?: boolean // blacks_rate/brokens_rate → black_and_broken_rate
}

const MAIN_FIELDS: MainField[] = [
  // ── Column 1 ──
  { key: 'lot_id', label: 'Lot ID', kind: 'text', required: true, pk: true },
  { key: 'lot_name', label: 'Lot Name', kind: 'text' },
  { key: 'lot_area', label: 'Lot Area', kind: 'combo', required: true },
  { key: 'no_of_bag', label: 'No Of Bag', kind: 'numeric' },
  { key: 'quantity_mt', label: 'Quantity KG', kind: 'numeric' },
  { key: 'cuptest', label: 'Cuptest', kind: 'text' },
  { key: 'mc_rate', label: 'MC.r', kind: 'numeric' },
  { key: 'blacks_rate', label: 'Blacks.r', kind: 'numeric', cascade: true },
  { key: 'brokens_rate', label: 'Brokens.r', kind: 'numeric', cascade: true },
  { key: 'black_and_broken_rate', label: 'Black And Broken.r', kind: 'numeric' },
  { key: 'fm_rate', label: 'FM.r', kind: 'numeric' },
  { key: 'mouldy_rate', label: 'Mouldy.r', kind: 'numeric' },
  // ── Column 2 ──
  { key: 'brown_rate', label: 'Brown.r', kind: 'numeric' },
  { key: 'partly_black_rate', label: 'Partly Black.r', kind: 'numeric' },
  { key: 'immature_rate', label: 'Immature.r', kind: 'numeric' },
  { key: 'broken_over_1_per_2_rate', label: 'Broken > 1/2.r', kind: 'numeric' },
  { key: 'insect_beans_rate', label: 'Insect.r', kind: 'numeric' },
  { key: 'triages_defects_rate', label: 'Triages Defects.r', kind: 'numeric' },
  { key: 'fb_rate', label: 'FB.r', kind: 'numeric' },
  { key: 'overdry_rate', label: 'Overdry.r', kind: 'numeric' },
  { key: 'cherry_rate', label: 'Cherry.r', kind: 'numeric' },
  { key: 'yellowbeans_rate', label: 'Yellowbeans.r', kind: 'numeric' },
  { key: 'polish_degree_rate', label: 'Polish Degree.r', kind: 'numeric' },
  { key: 's20_rate', label: 'S20.r', kind: 'numeric' },
  // ── Column 3 ──
  { key: 's19_rate', label: 'S19.r', kind: 'numeric' },
  { key: 's18_rate', label: 'S18.r', kind: 'numeric' },
  { key: 's16_rate', label: 'S16.r', kind: 'numeric' },
  { key: 's15_rate', label: 'S15.r', kind: 'numeric' },
  { key: 's14_rate', label: 'S14.r', kind: 'numeric' },
  { key: 's13_rate', label: 'S13.r', kind: 'numeric' },
  { key: 's12_rate', label: 'S12.r', kind: 'numeric' },
  { key: 'under_s12_rate', label: '< S12.r', kind: 'numeric' },
]

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Bg cho field `Enabled = NotDefault` (contract_* readonly + lot_id khi Edit).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
    fontWeight: 600,
  },
}

// Access `Format = "Standard"` = `#,##0.00` (thousand sep + 2 decimals).
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

// Parse user input ("1,234.5" / "1234.5" / "") → number | null.
function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Format raw value → Standard string (`1,234.56` / `''`).
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function distinctValues(rows: LotMgtRow[], field: string): string[] {
  const set = new Set<string>()
  for (const r of rows) {
    const v = String(r[field] ?? '').trim()
    if (v) set.add(v)
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'vi'))
}

interface LotMgtFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  row: LotMgtRow | null
  saving: boolean
  factoryCode: string
  existingRows: LotMgtRow[]
  onClose: () => void
  onSave: (payload: FactoryRowPayload) => void
}

export function LotMgtFormDialog({
  open,
  mode,
  row,
  saving,
  factoryCode,
  existingRows,
  onClose,
  onSave,
}: LotMgtFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)

  // Hydrate draft khi mở. New → empty. Edit → row values (numeric pre-format
  // Standard). Chỉ depend [open, mode, row] (memory feedback_hydrate_effect_deps).
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      const next: DraftState = { ...EMPTY_DRAFT }
      next.lot_id = String(row.lot_id ?? '')
      next.lot_name = String(row.lot_name ?? '')
      next.lot_area = String(row.lot_area ?? '')
      next.cuptest = String(row.cuptest ?? '')
      next.contract_no = String(row.contract_no ?? '')
      for (const k of NUMERIC_KEYS) {
        next[k] = formatStandard(row[k] as number | string | null)
      }
      setDraft(next)
    } else {
      setDraft(EMPTY_DRAFT)
    }
  }, [open, mode, row])

  const lotAreaOptions = useMemo(() => distinctValues(existingRows, 'lot_area'), [existingRows])

  // Contract suggest — Access contract_no combo + DLookUp (public.mv_local_s,
  // finalized_text='N'). Call API khi dialog mở; chọn contract_no → auto-fill
  // quality/client/package (readonly, không lưu — chỉ contract_no nằm trong strU/strI).
  const contractsQuery = useGetLotMgtContractsQuery({ factoryCode }, { skip: !open })
  const contracts = useMemo(() => contractsQuery.data ?? [], [contractsQuery.data])
  const contractNoOptions = useMemo(() => contracts.map((c) => c.contract_no), [contracts])
  // Combobox options: gồm cả contract_no đang lưu (Edit) nếu nó không còn trong
  // list finalized_text='N' → tránh warning "value not in options" + giữ value cũ.
  const contractNoSelectOptions = useMemo(() => {
    const cur = draft.contract_no.trim()
    if (cur && !contractNoOptions.includes(cur)) return [cur, ...contractNoOptions]
    return contractNoOptions
  }, [contractNoOptions, draft.contract_no])
  const contractByNo = useMemo(() => {
    const m = new Map<string, (typeof contracts)[number]>()
    for (const c of contracts) m.set(c.contract_no, c)
    return m
  }, [contracts])
  // Display readonly: ưu tiên lookup theo contract_no đang chọn; fallback row (Edit).
  const picked = contractByNo.get(draft.contract_no.trim())
  const contractQuality = picked
    ? picked.contract_quality ?? ''
    : mode === 'edit' && row
      ? String(row.contract_quality ?? '')
      : ''
  const contractClient = picked
    ? picked.contract_client ?? ''
    : mode === 'edit' && row
      ? String(row.contract_client ?? '')
      : ''
  const contractPackage = picked
    ? picked.contract_package ?? ''
    : mode === 'edit' && row
      ? String(row.contract_package ?? '')
      : ''

  const lotIdEmpty = draft.lot_id.trim().length === 0
  const lotAreaEmpty = draft.lot_area.trim().length === 0
  const canSave = !lotIdEmpty && !lotAreaEmpty && !saving

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // Auto-format Standard khi blur khỏi field số.
  const handleNumericBlur = (key: NumericKey) => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      return formatted !== prev[key] ? { ...prev, [key]: formatted } : prev
    })
  }

  // Access blacks_rate/brokens_rate AfterUpdate: black_and_broken_rate = sum.
  // Recompute trên blur (= thời điểm AfterUpdate commit) + format cả 2 field.
  const handleCascadeBlur = (key: 'blacks_rate' | 'brokens_rate') => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      const blacks = key === 'blacks_rate' ? formatted : prev.blacks_rate
      const brokens = key === 'brokens_rate' ? formatted : prev.brokens_rate
      const sum = (parseNumeric(blacks) ?? 0) + (parseNumeric(brokens) ?? 0)
      return {
        ...prev,
        [key]: formatted,
        black_and_broken_rate: formatStandard(sum),
      }
    })
  }

  // Map btnSave_Click: isValid(Me,strCheck) → Update_Insert(strU,strI,Me).
  // Build payload theo strI (new) — handleSaveEdit (parent) tự inject PK lot_id.
  const handleSave = () => {
    if (!canSave) return
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    const payload: FactoryRowPayload = {
      lot_id: draft.lot_id.trim(),
      lot_name: str(draft.lot_name),
      lot_area: draft.lot_area.trim(),
      cuptest: str(draft.cuptest),
    }
    for (const k of NUMERIC_KEYS) {
      payload[k] = parseNumeric(draft[k])
    }
    // contract_no: chỉ gửi khi có giá trị. List view (`view_factory_lot_mgt1`)
    // có thể không expose contract_no → Edit hydrate trống; gửi null sẽ xoá
    // giá trị đang lưu. Omit khi trống = giữ nguyên (BE update bỏ qua cột thiếu).
    const cn = draft.contract_no.trim()
    if (cn) payload.contract_no = cn
    onSave(payload)
  }

  // Map btnCancel_Click — Undo (draft unmount) + Close. Block khi saving.
  const handleClose = () => {
    if (saving) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(760px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
      >
        <Typography
          component="span"
          sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {mode === 'new' ? 'New' : 'Edit'} — Factory · Lot Mgt
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{ ...compactInputSx, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, pt: 1.5 }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          {/* Render theo MAIN_FIELDS (column-major) → DOM order = Tab order.
             col/row tính từ index: col = ⌊i/12⌋+1, row = i%12+1. */}
          {MAIN_FIELDS.map((f, i) => {
            const pos = {
              gridColumn: { xs: 1, sm: Math.floor(i / ROWS_PER_COL) + 1 },
              gridRow: { sm: (i % ROWS_PER_COL) + 1 },
            }
            if (f.kind === 'combo') {
              return (
                <Autocomplete<string, false, false, true>
                  key={f.key}
                  sx={pos}
                  freeSolo
                  size="small"
                  options={lotAreaOptions}
                  value={draft[f.key]}
                  onChange={(_, v) => handleChange(f.key, String(v ?? ''))}
                  onInputChange={(_, v) => handleChange(f.key, v)}
                  renderInput={(params) => (
                    <TextField {...params} label={f.label} required={f.required} fullWidth />
                  )}
                />
              )
            }
            if (f.kind === 'numeric') {
              const key = f.key as NumericKey
              const onBlur = f.cascade
                ? handleCascadeBlur(f.key as 'blacks_rate' | 'brokens_rate')
                : handleNumericBlur(key)
              return (
                <TextField
                  key={f.key}
                  sx={pos}
                  label={f.label}
                  size="small"
                  fullWidth
                  type="text"
                  value={draft[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  onBlur={onBlur}
                  inputProps={numericInputProps}
                />
              )
            }
            // text — lot_id (PK lock khi edit), lot_name, cuptest
            const lockedPk = !!f.pk && mode === 'edit'
            return (
              <TextField
                key={f.key}
                sx={{ ...pos, ...(lockedPk ? readonlyFieldSx : {}) }}
                label={f.label}
                size="small"
                fullWidth
                required={f.required}
                disabled={lockedPk}
                autoFocus={!!f.pk && mode === 'new'}
                value={draft[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                helperText={lockedPk ? 'Cannot be changed after creation.' : undefined}
              />
            )
          })}
        </Box>

        {/* Contract — hàng riêng dưới cùng. contract_no combo load suggest qua
           API; chọn → auto-fill quality/client/package (readonly DLookUp). */}
        <Divider sx={{ my: 1.5 }} />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          <Autocomplete<string, false, false, false>
            size="small"
            loading={contractsQuery.isFetching}
            options={contractNoSelectOptions}
            value={draft.contract_no || null}
            onChange={(_, v) => handleChange('contract_no', v ?? '')}
            renderOption={(props, option) => {
              const c = contractByNo.get(option)
              return (
                <li {...props} key={option}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option}
                    </Typography>
                    {c?.contract_client ? (
                      <Typography variant="caption" color="text.secondary">
                        {c.contract_client}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contract No"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {contractsQuery.isFetching ? <CircularProgress size={14} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            sx={readonlyFieldSx}
            label="Contract Quality"
            size="small"
            fullWidth
            value={contractQuality}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={readonlyFieldSx}
            label="Contract Client"
            size="small"
            fullWidth
            value={contractClient}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={readonlyFieldSx}
            label="Contract Packaging"
            size="small"
            fullWidth
            value={contractPackage}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default LotMgtFormDialog
